import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Sparkles, Pencil, X, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  adminTerms, adminUpdateTerm, adminDeleteTerm, adminGenerateTerm, listCategories, formatApiError,
} from "@/lib/inmindApi";
import CategoryBadge, { categoryLabel } from "@/components/CategoryBadge";

const DIFFICULTIES = ["beginner", "intermediate", "advanced"];

export default function AdminTermsPage() {
  const [terms, setTerms] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [busy, setBusy] = useState(false);
  const [genCat, setGenCat] = useState("");
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([adminTerms(), listCategories()]);
      setTerms(t); setCats(c);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load terms");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const t = await adminGenerateTerm(genCat || null);
      toast.success(`Generated: ${t.term}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await adminUpdateTerm(edit.id, {
        term: edit.term, meaning: edit.meaning, fact: edit.fact,
        category: edit.category, difficulty: edit.difficulty,
      });
      toast.success("Saved");
      setEdit(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!confirm(`Delete "${t.term}"? This also removes user progress + notes for this term.`)) return;
    try {
      await adminDeleteTerm(t.id);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = terms.filter((t) => {
    if (filterCat !== "all" && t.category !== filterCat) return false;
    if (q) {
      const ql = q.toLowerCase();
      return t.term.toLowerCase().includes(ql) || t.meaning.toLowerCase().includes(ql);
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="admin-terms-page">
      <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.7} />
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
              Generate a fresh term with AI
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={genCat}
              onChange={(e) => setGenCat(e.target.value)}
              data-testid="gen-category"
              className="h-10 rounded-xl border border-black/10 bg-white px-3 text-sm"
            >
              <option value="">Auto category</option>
              {cats.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
            </select>
            <Button
              onClick={generate}
              disabled={busy}
              data-testid="generate-term"
              className="rounded-xl bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.8} />}
              Generate term
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--inmind-muted)]" strokeWidth={1.7} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…" data-testid="admin-search" className="pl-9 h-11 rounded-xl" />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="admin-filter-cat"
          className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm"
        >
          <option value="all">All subjects</option>
          {cats.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[var(--inmind-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-terms-list">
          {filtered.map((t) => (
            <li key={t.id} className="bg-white rounded-2xl border border-black/5 p-4 shadow-[0_4px_18px_rgba(0,0,0,0.03)]" data-testid={`admin-term-${t.id}`}>
              {edit?.id === t.id ? (
                <div className="space-y-2">
                  <Input value={edit.term} onChange={(e) => setEdit({ ...edit, term: e.target.value })} placeholder="Term" />
                  <Textarea value={edit.meaning} onChange={(e) => setEdit({ ...edit, meaning: e.target.value })} placeholder="Meaning" className="min-h-[80px]" />
                  <Textarea value={edit.fact} onChange={(e) => setEdit({ ...edit, fact: e.target.value })} placeholder="Fact" className="min-h-[80px]" />
                  <div className="flex gap-2">
                    <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="flex-1 h-10 rounded-lg border border-black/10 bg-white px-2 text-sm">
                      {cats.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                    </select>
                    <select value={edit.difficulty} onChange={(e) => setEdit({ ...edit, difficulty: e.target.value })} className="flex-1 h-10 rounded-lg border border-black/10 bg-white px-2 text-sm">
                      {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={saveEdit} disabled={busy} data-testid={`admin-save-${t.id}`} className="rounded-lg bg-[var(--inmind-primary)] text-white text-sm gap-1">
                      <Save className="w-3.5 h-3.5" strokeWidth={1.8} /> Save
                    </Button>
                    <Button onClick={() => setEdit(null)} variant="ghost" className="rounded-lg text-sm">
                      <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <CategoryBadge category={t.category} />
                    <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-muted)]">{t.difficulty}</span>
                  </div>
                  <h3 className="font-serif text-lg text-[var(--inmind-text)]">{t.term}</h3>
                  <p className="text-sm text-[var(--inmind-text-secondary)] mt-1 line-clamp-2">{t.meaning}</p>
                  <p className="text-xs text-[var(--inmind-muted)] mt-2 line-clamp-2">{t.fact}</p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setEdit({ ...t })}
                      data-testid={`admin-edit-${t.id}`}
                      aria-label="Edit"
                      className="p-1.5 rounded-lg text-[var(--inmind-muted)] hover:bg-black/5 hover:text-[var(--inmind-primary)]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </button>
                    <button
                      onClick={() => remove(t)}
                      data-testid={`admin-delete-${t.id}`}
                      aria-label="Delete"
                      className="p-1.5 rounded-lg text-[var(--inmind-muted)] hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
